import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

class UnilarCompreJunto extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.#onSelectionChange);
    this.addEventListener('click', this.#onClick);
    this.#updateState();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onSelectionChange);
    this.removeEventListener('click', this.#onClick);
  }

  #onSelectionChange = () => {
    this.#updateState();
  };

  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('[data-compre-junto-submit]');
    if (!(button instanceof HTMLButtonElement) || !this.contains(button)) return;

    this.#onSubmit(event);
  };

  #onSubmit = async (event) => {
    event.preventDefault();

    const button = this.querySelector('[data-compre-junto-submit]');
    const selectedItems = this.#selectedItems();
    if (!(button instanceof HTMLButtonElement) || button.disabled || selectedItems.length === 0) return;

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    this.#setMessage('');

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    }

    /** @type {{ items: Array<{ id: number, quantity: number }>, sections?: string, sections_url?: string }} */
    const payload = {
      items: selectedItems.map((item) => ({
        id: Number(item.dataset.variantId),
        quantity: 1,
      })),
    };

    if (cartItemComponentsSectionIds.length > 0) {
      payload.sections = cartItemComponentsSectionIds.join(',');
      payload.sections_url = window.location.pathname;
    }

    try {
      const response = await fetch(this.#cartAddUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json();

      if (data.status) {
        this.dispatchEvent(new CartErrorEvent(this.id, data.message, data.description, data.errors));
        this.#setMessage(data.message || 'Não foi possível adicionar os produtos ao carrinho.');
        return;
      }

      const itemCount = selectedItems.length;
      const cart = await this.#fetchCart();

      this.dispatchEvent(
        new CartAddEvent(cart || data, this.id, {
          source: cart ? 'unilar-compre-junto' : 'product-form-component',
          itemCount: cart?.item_count ?? itemCount,
          sections: data.sections,
        })
      );

      this.#setMessage(Theme.translations.added || 'Adicionado');
    } catch (error) {
      console.error(error);
      this.#setMessage('Não foi possível adicionar os produtos ao carrinho.');
    } finally {
      button.removeAttribute('aria-busy');
      this.#updateState();
    }
  };

  #selectedItems() {
    return Array.from(this.querySelectorAll('[data-compre-junto-item]:checked')).filter((item) => {
      return item instanceof HTMLInputElement && item.dataset.available !== 'false' && item.dataset.variantId;
    });
  }

  #discountBase(item) {
    const regularPrice = Number(item.dataset.regularPrice || 0);
    const compareAtPrice = Number(item.dataset.compareAtPrice || 0);
    return compareAtPrice > regularPrice ? compareAtPrice : regularPrice;
  }

  #updateState() {
    const inputs = Array.from(this.querySelectorAll('[data-compre-junto-item]'));
    const selectedItems = this.#selectedItems();
    const subtotal = selectedItems.reduce((total, item) => total + Number(item.dataset.price || 0), 0);
    const originalTotal = selectedItems.reduce((total, item) => total + this.#discountBase(item), 0);
    const savings = Math.max(originalTotal - subtotal, 0);
    const discountPercent = originalTotal > 0 ? Math.floor((savings * 100) / originalTotal) : 0;

    for (const input of inputs) {
      if (!(input instanceof HTMLInputElement)) continue;
      input.closest('.unilar-card-compre-junto')?.classList.toggle('is-selected', input.checked);
    }

    this.#setText('[data-compre-junto-count]', selectedItems.length.toString());
    this.#setText('[data-compre-junto-total]', this.#formatMoney(subtotal));
    this.#setText('[data-compre-junto-installment]', this.#formatMoney(Math.floor(subtotal / Number(this.dataset.installments || 10))));

    const savingsElement = this.querySelector('[data-compre-junto-savings]');
    const regularTotalElement = this.querySelector('[data-compre-junto-regular-total]');
    const discountRow = this.querySelector('[data-compre-junto-discount-row]');

    if (savingsElement instanceof HTMLElement) {
      savingsElement.textContent = `${discountPercent}% OFF`;
    }

    if (regularTotalElement instanceof HTMLElement) {
      regularTotalElement.textContent = this.#formatMoney(originalTotal);
    }

    if (discountRow instanceof HTMLElement) {
      discountRow.hidden = discountPercent <= 0;
    }

    const button = this.querySelector('[data-compre-junto-submit]');
    if (button instanceof HTMLButtonElement && button.getAttribute('aria-busy') !== 'true') {
      button.disabled = selectedItems.length === 0;
    }
  }

  #formatMoney(value) {
    const moneyFormat = this.dataset.moneyFormat || 'R$ {{amount_with_comma_separator}}';
    const currency = this.dataset.currency || 'BRL';
    return formatMoney(value, moneyFormat, currency);
  }

  #cartAddUrl() {
    if (window.Theme?.routes?.cart_add_url) return window.Theme.routes.cart_add_url;
    return `${window.Shopify?.routes?.root || '/'}cart/add.js`;
  }

  async #fetchCart() {
    try {
      const cartUrl = window.Theme?.routes?.cart_url ? `${window.Theme.routes.cart_url}.js` : `${window.Shopify?.routes?.root || '/'}cart.js`;
      const response = await fetch(cartUrl, { headers: { Accept: 'application/json' } });
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  #setText(selector, value) {
    const element = this.querySelector(selector);
    if (element instanceof HTMLElement) {
      element.textContent = value;
    }
  }

  #setMessage(message) {
    const element = this.querySelector('[data-compre-junto-message]');
    if (element instanceof HTMLElement) {
      element.textContent = message;
    }
  }
}

if (!customElements.get('unilar-compre-junto')) {
  customElements.define('unilar-compre-junto', UnilarCompreJunto);
}
